import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Navigation, ArrowLeft, Mail, KeyRound, Check } from "lucide-react";

export default function ForgotPasswordPage() {
  // A one-time link from the company admin (?token=...) skips straight to choosing a password
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("token") || "";
  const [step, setStep] = useState(linkToken ? 3 : 1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState(linkToken);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [demoCode, setDemoCode] = useState(""); // For demo purposes
  const navigate = useNavigate();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await apiClient.post("/auth/forgot-password", { email });
      toast.success(response.data.message || "If an account exists with this email, a reset code has been sent.");
      
      // Preview environment only - the live server never returns a code
      if (response.data.demo_code) {
        setDemoCode(response.data.demo_code);
      }
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await apiClient.post(`/auth/verify-reset-code?email=${encodeURIComponent(email)}&code=${code}`);
      setResetToken(response.data.token);
      toast.success("Code verified! Set your new password.");
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setIsLoading(true);

    try {
      await apiClient.post("/auth/reset-password", {
        token: resetToken,
        new_password: newPassword
      });
      toast.success("Password reset successful! You can now login.");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-indigo-900 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white rounded-full"></div>
          <div className="absolute bottom-40 right-10 w-96 h-96 border border-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-white rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-lime-400 rounded-xl flex items-center justify-center">
              <Navigation className="w-6 h-6 text-indigo-900" />
            </div>
            <h1 className="text-3xl font-heading font-black text-white tracking-tight">There You Are</h1>
          </div>
          <p className="text-indigo-200 text-lg">Real-time ride scheduling & tracking</p>
        </div>
        
        <div className="relative z-10 space-y-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-lime-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-5 h-5 text-lime-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Secure Reset</h3>
              <p className="text-indigo-200 text-sm">Use a one-time code, or a one-time link from your company admin</p>
            </div>
          </div>
        </div>
        
        <p className="text-indigo-300 text-sm relative z-10">© 2024 There You Are. All rights reserved.</p>
      </div>
      
      {/* Right side - Reset Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-indigo-900 rounded-xl flex items-center justify-center">
                <Navigation className="w-5 h-5 text-lime-400" />
              </div>
              <span className="text-xl font-heading font-black text-indigo-900">There You Are</span>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s ? "bg-indigo-900 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {step > s ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-8 h-1 mx-1 rounded ${step > s ? "bg-indigo-900" : "bg-slate-200"}`} />
                  )}
                </div>
              ))}
            </div>
            
            <CardTitle className="text-2xl font-heading font-bold text-slate-900">
              {step === 1 && "Forgot Password?"}
              {step === 2 && "Enter Reset Code"}
              {step === 3 && "Set New Password"}
            </CardTitle>
            <CardDescription className="text-slate-600">
              {step === 1 && "Enter your email and we'll send you a 6-digit reset code"}
              {step === 2 && "Enter the 6-digit code we sent to your email"}
              {step === 3 && "Choose a new secure password"}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Step 1: Email */}
            {step === 1 && (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                      className="h-12 pl-10 bg-slate-50 border-slate-300 focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-indigo-900 hover:bg-indigo-800 text-white font-semibold rounded-lg shadow-md transition-all active:scale-95"
                  disabled={isLoading}
                  data-testid="forgot-submit-button"
                >
                  {isLoading ? "Sending..." : "Send Reset Code"}
                </Button>
              </form>
            )}
            
            {/* Step 2: Verification Code */}
            {step === 2 && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                {demoCode && (
                  <div className="bg-lime-50 border border-lime-200 rounded-lg p-3 mb-4">
                    <p className="text-lime-800 text-sm font-medium">Demo Mode: Your code is <span className="font-mono font-bold">{demoCode}</span></p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-slate-700">Reset Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    data-testid="forgot-code-input"
                    className="h-12 bg-slate-50 border-slate-300 focus:ring-2 focus:ring-indigo-500 text-center text-2xl font-mono tracking-widest"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-indigo-900 hover:bg-indigo-800 text-white font-semibold rounded-lg shadow-md transition-all active:scale-95"
                  disabled={isLoading || code.length !== 6}
                  data-testid="verify-code-button"
                >
                  {isLoading ? "Verifying..." : "Verify Code"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setStep(1)}
                >
                  Didn't receive code? Try again
                </Button>
              </form>
            )}
            
            {/* Step 3: New Password */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-700">New Password</Label>
                  <PasswordInput
                    id="newPassword"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                    className="h-12 bg-slate-50 border-slate-300 focus:ring-2 focus:ring-indigo-500"
                    toggleClassName="text-slate-500 hover:text-slate-800 focus-visible:text-indigo-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                  <PasswordInput
                    id="confirmPassword"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                    className="h-12 bg-slate-50 border-slate-300 focus:ring-2 focus:ring-indigo-500"
                    toggleClassName="text-slate-500 hover:text-slate-800 focus-visible:text-indigo-600"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-indigo-900 hover:bg-indigo-800 text-white font-semibold rounded-lg shadow-md transition-all active:scale-95"
                  disabled={isLoading}
                  data-testid="reset-password-button"
                >
                  {isLoading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            )}
            
            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 text-indigo-900 font-semibold hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
